import React, { useMemo } from 'react'
import { snakeCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import {
  Avatar,
  Button,
  Box as Container,
  DataTable,
  DataTableCellVariants,
  DataTableColumnAlignments,
  DataTableRowData,
  DropdownMenu,
  DropdownMenuItem,
  Icon,
  Pagination,
  RowsPerPageType,
  RowsSkeletonLoader,
  useSidePanel,
  Box as Wrapper,
} from '@node-space/ui-components'
import { useBeneficiariesQuery } from 'hooks/queries/useBeneficiariesQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { usePaginationNextPageItemCount } from 'hooks/usePaginationNextPageItemCount'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { useSimplePagination } from 'hooks/useSimplePagination'
import { BeneficiaryDetails } from 'types/beneficiaries'
import { getBeneficiaryName, getBeneficiaryType, getEntityType } from 'utils/beneficiaries'
import { BeneficiaryEmptyState } from './BeneficiaryEmptyState'
import { BeneficiaryErrorState } from './BeneficiaryErrorState'
import { BeneficiarySummary } from './BeneficiarySummary'

interface BeneficiaryTableProps {
  setBeneficiaryToDelete: React.Dispatch<React.SetStateAction<BeneficiaryDetails>>
  setIsAddModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  handleEditBeneficiaryName: (beneficiary: BeneficiaryDetails) => void
}

export const BeneficiaryTable = ({
  setBeneficiaryToDelete,
  setIsAddModalOpen,
  handleEditBeneficiaryName,
}: BeneficiaryTableProps) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const sizeMax = +searchParams.get('size')

  const getValidRowsPerPage = (size: number): RowsPerPageType => {
    const validSizes: RowsPerPageType[] = [10, 20, 30, 40, 50, 100]
    return validSizes.includes(size as RowsPerPageType) ? (size as RowsPerPageType) : 10
  }

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = useSimplePagination(
    getValidRowsPerPage(sizeMax)
  )

  const {
    data: { content: beneficiaryList, pagination } = {},
    isPending,
    isError,
  } = useBeneficiariesQuery({
    page,
    size: rowsPerPage,
  })

  const {
    isSidePanelOpen: isBeneficiaryDetailsOpen,
    sidePanelData: beneficiaryDetails,
    paging: pageBeneficiaries,
    setIsSidePanelOpen: setIsBeneficiaryDetailsOpen,
    updateSidePanel: updateBeneficiaryDetails,
  } = useSidePanel<BeneficiaryDetails>(beneficiaryList)

  const { paging, showPagination } = usePaginationPagingPersist(pagination)
  const permissions = useMappedRoles().manageBeneficiaries

  const canEdit = permissions?.canEdit

  const columns = useMemo(() => {
    return [
      { key: 'accountHolder', label: t('accountHolder'), variant: DataTableCellVariants.TEXT },
      { key: 'currency', label: t('currency'), variant: DataTableCellVariants.TEXT },
      {
        key: 'beneficiaryType',
        label: t('manageBeneficiaries.beneficiaryType'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'entityType',
        label: t('manageBeneficiaries.entityType'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'actions',
        label: '',
        variant: DataTableCellVariants.ACTION,
        align: DataTableColumnAlignments.RIGHT,
      },
    ]
  }, [])

  const onOpenBeneficiaryDetails = (beneficiaryIndex: number) => {
    updateBeneficiaryDetails(beneficiaryIndex)
    setIsBeneficiaryDetailsOpen(true)
  }

  const tableData = useMemo((): DataTableRowData[] => {
    return beneficiaryList?.map((beneficiary: BeneficiaryDetails, index: number) => {
      return {
        id: beneficiary?.reference,
        testid: beneficiary?.reference,
        onRowClick: () => {
          onOpenBeneficiaryDetails(index)
        },
        cells: [
          {
            key: 'accountHolder',
            id: `beneficiary-${snakeCase(getBeneficiaryName(beneficiary))}-title`,
            variant: 'TEXT',
            Text: `${getBeneficiaryName(beneficiary)} ${beneficiary?.alias ? ` (${beneficiary?.alias})` : ''}`,
            BottomText: beneficiary?.fiat?.accountNumber,
          },
          {
            key: 'currency',
            id: `beneficiary-${snakeCase(getBeneficiaryName(beneficiary))}-currency`,
            variant: DataTableCellVariants.TEXT,
            Avatar: <Avatar type="currency" name={beneficiary?.currency} />,
            Text: beneficiary?.currency?.replace('Fdusd', 'FDUSD'),
          },
          {
            key: 'beneficiaryType',
            id: `beneficiary-${snakeCase(getBeneficiaryName(beneficiary))}-beneficiary-type`,
            variant: DataTableCellVariants.TEXT,
            Text: getBeneficiaryType(beneficiary?.beneficiaryType, t),
          },
          {
            key: 'entityType',
            id: `beneficiary-${snakeCase(getBeneficiaryName(beneficiary))}-entity-type`,
            variant: DataTableCellVariants.TEXT,
            Text: getEntityType(beneficiary?.entityType, t),
          },
          {
            key: 'actions',
            variant: DataTableCellVariants.ACTION,
            Actions: canEdit
              ? [
                  <DropdownMenu
                    key={`context-menu-${beneficiary?.reference}`}
                    DropdownMenuItems={[
                      <DropdownMenuItem
                        key="details"
                        LeadingIcon={<Icon name="Info" />}
                        onClick={() => onOpenBeneficiaryDetails(index)}
                      >
                        {t('details')}
                      </DropdownMenuItem>,
                      <DropdownMenuItem
                        key="delete"
                        LeadingIcon={<Icon name="Trash" />}
                        onClick={() => setBeneficiaryToDelete(beneficiary)}
                      >
                        {t('delete')}
                      </DropdownMenuItem>,
                      <DropdownMenuItem
                        key="edit"
                        LeadingIcon={<Icon name="SimplePencilCustom" />}
                        onClick={() => handleEditBeneficiaryName(beneficiary)}
                      >
                        {t('manageBeneficiaries.editBeneficiary')}
                      </DropdownMenuItem>,
                    ]}
                  >
                    <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
                  </DropdownMenu>,
                ]
              : [],
          },
        ],
      }
    })
  }, [beneficiaryList])

  const nextPageRowCount = usePaginationNextPageItemCount(paging)

  return (
    <Wrapper tag="span" name="container-beneficiary">
      {isError ? (
        <BeneficiaryErrorState onRefresh={() => window.location.reload()} />
      ) : (
        <>
          <Container
            tag="span"
            className={`${isPending && 'border-primaryTint'} rounded middle grid`}
          >
            {isPending ? (
              <RowsSkeletonLoader rows={nextPageRowCount} />
            ) : (
              <>
                {beneficiaryList?.length ? (
                  <>
                    <DataTable headers={columns} data={tableData} />
                    <BeneficiarySummary
                      isOpen={isBeneficiaryDetailsOpen}
                      beneficiaryDetails={beneficiaryDetails}
                      pageBeneficiaries={pageBeneficiaries}
                      onClose={() => setIsBeneficiaryDetailsOpen(false)}
                    />
                  </>
                ) : (
                  <BeneficiaryEmptyState onAddBeneficiary={() => setIsAddModalOpen(true)} />
                )}
              </>
            )}
          </Container>

          {!!paging && !showPagination && (
            <Container className={`${isPending || showPagination ? 'opacity-25' : ''}`}>
              <Pagination
                testId="beneficiary-pagination"
                page={page}
                rowsPerPage={rowsPerPage}
                total={paging?.total ?? 0}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </Container>
          )}
        </>
      )}
    </Wrapper>
  )
}

export default BeneficiaryTable
